import { Button, Typography, Grid2, Paper, Box, List, ListItem } from '@mui/material'
import { useEffect, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { applyDataSources, selectAppliedDataSources, selectDataSources } from '../../store/slices/dataSourcesSlice';

import DataSourceButton from '../styled/DataSourceButton';
import { convertToPixelFromVH, convertToPixelFromVW } from '../../utils/ViewportUtils';
import { SCROLL_BAR_STYLE_PROPS } from '../../constants/ScrollbarProps';

function DataSources({ inline = true, disable = false }: { inline?: boolean, disable?: boolean }) {
    const [expanded, setExpanded] = useState(false);
    const dispatch = useDispatch<AppDispatch>();

    const availableDataSourcesInSystem = useSelector(selectDataSources);
    const appliedDataSources = useSelector(selectAppliedDataSources);
    const [selectedInsideSourcesDropdown, setSelectedInsideSourcesDropdown] = useState<any[]>(appliedDataSources);
    const allSourcesSelected = () => appliedDataSources.length === availableDataSourcesInSystem.length;

    const handleExpand = () => {
        setExpanded(previous => !previous)
    };

    const handleSelectAll = () => setSelectedInsideSourcesDropdown([...availableDataSourcesInSystem]);
    const handleClearAll = () => setSelectedInsideSourcesDropdown([])

    const isSelected = (source: any, list: any[]) => list.some((s: any) => s.id === source.id);

    const toggleSource = (source: any) => {
        if (selectedInsideSourcesDropdown.filter((s: any) => s.id === source.id).length === 0)
            setSelectedInsideSourcesDropdown(previous => [...previous, source]);
        else
            setSelectedInsideSourcesDropdown(previous => previous.filter(s => s !== source));
    }

    useEffect(() => {
        dispatch(applyDataSources(selectedInsideSourcesDropdown))
    }, [selectedInsideSourcesDropdown, dispatch])

    return (
        <Grid2 container
            sx={{
                my: { xs: 2 },
                width: { xs: "100%" },
                display: 'flex',
                flexDirection: inline ? 'row' : "column",
                alignItems: inline ? 'center' : 'end',
                gap: 1,
                position: 'relative',
            }}>
            <Box sx={{
                display: 'flex',
                flexDirection: "column",
                alignItems: inline ? 'start' : 'end',
            }}>
                {expanded && <Box sx={{
                    zIndex: 1,
                    filter: 'drop-shadow(0 0 5px #0090B3)',
                }}>
                    <Paper sx={{
                        position: 'absolute',
                        bottom: 0,
                        transform: 'translate(0%, 0%)',
                        zIndex: 1,
                        width: '17.5vw',
                        height: '32.5vh',
                        padding: 1,
                        boxSizing: 'border-box',
                        display: 'grid',
                        gridTemplateRows: '90% 10%',
                        rowGap: 1,
                        clipPath: `path('M 0 0 H ${convertToPixelFromVW(16)} L ${convertToPixelFromVW(17.5)} ${convertToPixelFromVH(3)} V ${convertToPixelFromVH(32.5)} H 0 Z')`,
                    }}>
                        <List sx={{
                            height: '100%',
                            overflowY: 'scroll',
                            overflowX: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.25,
                            ...SCROLL_BAR_STYLE_PROPS,
                        }}>
                            {availableDataSourcesInSystem.length === 0 &&
                                <ListItem sx={{ width: '90%', padding: 0, }}>
                                    <Typography>No data sources available.</Typography>
                                </ListItem>}
                            {availableDataSourcesInSystem.length > 0 && availableDataSourcesInSystem.map((source: any) => {
                                const isThisSelected = isSelected(source, selectedInsideSourcesDropdown);
                                return (
                                    <ListItem
                                        key={source.id}
                                        sx={{
                                            width: '95%',
                                            padding: 0,
                                        }}>
                                        <DataSourceButton
                                            onClick={() => toggleSource(source)}
                                            radius={0}
                                            background={isThisSelected ? '#98D6E580' : 'transparent'}
                                            fullWidth
                                            sx={{
                                                boxShadow: isThisSelected ? '' : 'none !important',
                                            }}>
                                            {isThisSelected ? <CheckIcon /> : <></>}
                                            <Typography variant='body1' fontWeight={500}>
                                                {source.name}
                                            </Typography>
                                        </DataSourceButton>
                                    </ListItem>
                                )
                            })}
                        </List>
                        {availableDataSourcesInSystem.length > 0 && <Button variant='text'
                            onClick={allSourcesSelected() ? handleClearAll : handleSelectAll}
                            sx={{
                                width: 'max-content',
                                padding: 0,
                                color: '#00A9B1',
                                fontWeight: 700,
                                textDecoration: 'underline',
                                display: 'grid',
                                placeContent: 'center',
                                marginBottom: '0.5vw',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}>
                            <Typography variant='body1' fontWeight={500}>
                                {allSourcesSelected() ? 'Clear All' : 'Select All'}
                            </Typography>
                        </Button>}
                    </Paper></Box>}
                <Box sx={{
                    display: 'flex',
                    flexDirection: "row",
                    alignItems: 'center',
                    gap: 2,
                }}>
                    <DataSourceButton
                        disabled={disable}
                        onClick={handleExpand}
                        radius={20}
                        sx={{
                            width: 'max-content',
                            paddingY: '0.5vw',
                            paddingX: '1vw !important',
                            position: "relative",
                            zIndex: 100,
                            borderRadius: '2vw !important',
                            border: '0.125vw solid #2E79B3CC',
                        }}>
                        <Typography variant='body1' fontWeight={500}>
                            {appliedDataSources.length === 0 ? 'Search against all data-sets' : 'Search against'}
                        </Typography>
                        {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </DataSourceButton>
                </Box>
            </Box>
            <Grid2 sx={{
                display: 'contents'
            }}>
                {appliedDataSources.length > 0 && appliedDataSources.map((source: any) => {
                    return (
                        <DataSourceButton
                            radius={20}
                            background={'#98D6E580'}
                            key={source.id}
                            sx={{
                                boxSizing: 'border-box',
                                paddingY: '0.125vw',
                                border: '0.125vw solid #2E79B3CC',
                            }}>
                            <CheckIcon />
                            <Typography variant='body1' fontWeight={500}>
                                {source.name}
                            </Typography>
                        </DataSourceButton>
                    )
                })}
            </Grid2>
        </Grid2>
    )
}

export default DataSources